import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSpreadsheet, Flag, GraduationCap, Newspaper, RefreshCw, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { EmptyState, ErrorState, LoadingState, Notice } from "@/components/common/States";
import { Composer } from "@/components/feed/Composer";
import { ModerationPanel } from "@/components/feed/ModerationPanel";
import { PostCard } from "@/components/feed/PostCard";
import { ReportDialog } from "@/components/feed/ReportDialog";
import { useAuth, useSchoolCan } from "@/context/AuthContext";
import { fetcher } from "@/lib/api";
import { TENANT_STATUS } from "@/lib/labels";
import { SchoolSwitcher } from "@/components/layout/SchoolSwitcher";

export const ReadOnlyBanner = () => {
  const { me } = useAuth();
  const a = me?.active_school;
  if (!a) return null;
  if (a.status !== "active") return <Notice tone="warn" testId="readonly-banner">Keanggotaan Anda nonaktif. Anda dapat melihat data, tetapi semua perubahan ditolak oleh server (mode baca saja).</Notice>;
  if (a.school_status !== "Active") return <Notice tone="warn" testId="tenant-status-banner">Status sekolah: {TENANT_STATUS[a.school_status]?.label}. Perubahan data belum diizinkan sampai Admin Platform mengaktifkan sekolah.</Notice>;
  return null;
};

const SHORTCUT_ITEMS = [
  { to: "/sekolah/akademik", label: "Data Akademik", icon: GraduationCap, testId: "nav-school-academic" },
  { to: "/sekolah/penilaian", label: "Penilaian & Rapor", icon: FileSpreadsheet, testId: "nav-school-grades" },
  { to: "/sekolah/presensi", label: "Presensi", icon: ScanLine, testId: "nav-school-attendance" },
];

const ShortcutRow = () => (
  <TooltipProvider delayDuration={200}>
    <div className="flex border-b bg-card" data-testid="page-shortcuts">
      {SHORTCUT_ITEMS.map((n, i) => (
        <Tooltip key={n.to}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              asChild
              className={`flex-1 h-12 sm:h-11 flex-col sm:flex-row gap-0.5 sm:gap-0 rounded-none text-foreground/60 hover:bg-muted hover:text-foreground ${i < SHORTCUT_ITEMS.length - 1 ? "border-r" : ""}`}
              data-testid={`shortcut-${n.testId}`}
            >
              <NavLink to={n.to}>
                <n.icon className="h-5 w-5" aria-hidden />
                <span className="text-[10px] leading-none sm:hidden">{n.label.split(" ")[0]}</span>
              </NavLink>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">{n.label}</TooltipContent>
        </Tooltip>
      ))}
    </div>
  </TooltipProvider>
);

export default function SchoolHome() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const bisaModerasi = useSchoolCan("feed:moderate");
  const [lapor, setLapor] = useState(null);
  const [tab, setTab] = useState("feed");

  const ctx = useQuery({ queryKey: ["feed-context"], queryFn: () => fetcher("/school/feed/context") });
  const ringkas = useQuery({ queryKey: ["school-overview"], queryFn: () => fetcher("/school/overview") });
  const moderasi = useQuery({
    queryKey: ["feed-moderation"], queryFn: () => fetcher("/school/feed/moderation"),
    enabled: bisaModerasi, refetchInterval: 120000,
  });
  const feed = useInfiniteQuery({
    queryKey: ["feed-posts"],
    queryFn: ({ pageParam }) => fetcher(`/school/feed/posts?limit=10${pageParam ? `&before=${encodeURIComponent(pageParam)}` : ""}`),
    initialPageParam: null,
    getNextPageParam: (last) => last.next,
  });

  const segarkan = () => {
    qc.invalidateQueries({ queryKey: ["feed-posts"] });
    qc.invalidateQueries({ queryKey: ["feed-context"] });
    qc.invalidateQueries({ queryKey: ["feed-moderation"] });
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  if (ctx.isLoading || ringkas.isLoading) return <LoadingState rows={4} />;
  if (ctx.error) return <ErrorState error={ctx.error} onRetry={ctx.refetch} />;
  if (ringkas.error) return <ErrorState error={ringkas.error} onRetry={ringkas.refetch} />;
  const c = ctx.data;
  const posts = feed.data?.pages.flatMap((p) => p.posts) ?? [];
  const menunggu = moderasi.data?.open_reports ?? 0;
  const moderator = c.can_moderate && bisaModerasi;

  return (
    <div className="mx-auto max-w-2xl">
      {/* School header */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3">
        <div className="flex-1 min-w-0">
          <SchoolSwitcher />
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" onClick={segarkan} data-testid="feed-refresh-button" aria-label="Muat ulang">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <ReadOnlyBanner />

      {/* Icon navigation bar */}
      <ShortcutRow />

      {/* Content area */}
      <div className="px-3 sm:px-4 py-2.5 sm:py-3 space-y-2.5 sm:space-y-3">
        {c.can_publish ? (
          <Composer ctx={c} onPublished={segarkan} />
        ) : (
          <Notice tone="muted" testId="feed-read-only">
            Hanya guru, wali kelas, kepala sekolah, operator, dan Admin Sekolah yang dapat menerbitkan postingan.
          </Notice>
        )}

        {/* Feed tabs */}
        {moderator && (
          <div className="flex gap-1.5" role="group" aria-label="Tampilan feed">
            <Button size="sm" variant={tab === "feed" ? "default" : "ghost"} className="h-8 px-3" onClick={() => setTab("feed")} data-testid="feed-tab-feed">
              <Newspaper className="mr-1.5 h-3.5 w-3.5" /> Feed
            </Button>
            <Button size="sm" variant={tab === "moderasi" ? "default" : "ghost"} className="h-8 px-3" onClick={() => setTab("moderasi")} data-testid="feed-tab-moderation">
              <Flag className="mr-1.5 h-3.5 w-3.5" /> Perlu ditinjau
              {menunggu > 0 && <span className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#E11D2E] px-1 text-[9px] font-bold text-white" data-testid="feed-moderation-badge">{menunggu}</span>}
            </Button>
          </div>
        )}

        {/* Feed content */}
        {tab === "moderasi" && moderator ? <ModerationPanel onRefresh={segarkan} /> : (
          <>
            {feed.isLoading ? <LoadingState rows={3} /> : feed.error ? <ErrorState error={feed.error} onRetry={feed.refetch} />
              : !posts.length ? (
                <EmptyState title="Belum ada postingan"
                  description={c.can_publish
                    ? "Mulai dengan mengumumkan kegiatan sekolah hari ini."
                    : "Pengumuman sekolah dan postingan guru akan muncul di sini."} />
              ) : (
                <div className="space-y-2.5 sm:space-y-3" data-testid="feed-list">
                  {posts.map((p) => <PostCard key={p.id} post={p} ctx={c} onReport={setLapor} onRefresh={segarkan} />)}
                </div>
              )}
            {feed.hasNextPage && (
              <Button variant="outline" className="w-full" disabled={feed.isFetchingNextPage}
                onClick={() => feed.fetchNextPage()} data-testid="feed-load-more">
                {feed.isFetchingNextPage ? "Memuat…" : "Muat postingan sebelumnya"}
              </Button>
            )}
          </>
        )}
      </div>

      {lapor && <ReportDialog target={lapor} open={!!lapor} onOpenChange={(v) => !v && setLapor(null)} />}
    </div>
  );
}
